/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback } from 'react';
import { style as s } from '../style';
import DataAddModal from './DataAddModal';
import { ReactComponent as PlusIcon }from 'assets/icons/icon_plus.svg';
import { ExpandLess, ExpandMore, Visibility, VisibilityOff, DeleteOutline, DragIndicator, Tune, TableChart } from '@mui/icons-material';
import { MenuItem, Slider, TextField, Paper, Stack, Typography, IconButton, Collapse, Divider, Box, Button } from '@mui/material';
import * as config from 'config/myMap/config';
import * as layerUtils from 'utils/myMap/layerUtils';

const LayerContent = ({
    layers, 
    selectedLayerIndex, 
    setSelectedLayerIndex, 
    updateLayer, 
    removeLayer, 
    reorderLayers,
    isTableExpanded,
    setIsAttributeTableExpanded,
    
    // 파일/모달 관련 Props
    fileInputRef,
    isUploadModalOpen,
    isDragActive,
    openUploadModal,
    closeUploadModal,
    handleFileUpload,
    handleClick,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleDownload1,
    handleDownload2,
}) => {

    const [expandedMap, setExpandedMap] = useState({});
    const [draggedIndex, setDraggedIndex] = useState(null);

    const propertyOptions = useMemo(
        () =>
            layers.map((layer) => {
                const firstFeature = layer?.geojson?.features?.[0];
                return firstFeature ? Object.keys(firstFeature.properties ?? {}) : [];
            }),
        [layers]
    );

    const geometryPresence = useMemo(
        () =>
            layers.map((layer) => {
                const types = new Set();
                layer?.geojson?.features?.forEach((feature) => {
                    const geomType = feature?.geometry?.type?.toLowerCase();
                    if (!geomType) return;
                    if (geomType.includes('point')) types.add('point');
                    if (geomType.includes('line') || geomType.includes('multilinestring')) types.add('line');
                    if (geomType.includes('poly')) types.add('polygon');
                });
                return types;
            }),
        [layers]
    );

    const toggleExpand = useCallback(
        (index) => {
            setExpandedMap((prev) => ({ ...prev, [index]: !prev[index] }));
        },
        [setExpandedMap]
    );

    const handleVisibilityToggle = useCallback(
        (index) => {
            const layer = layers[index];
            updateLayer(index, { visible: !layer.visible });
        },
        [layers, updateLayer]
    );

    const handleLayerSelect = useCallback(
        (index) => {
            setSelectedLayerIndex(index);
            setExpandedMap((prev) => ({ ...prev, [index]: true }));

            // 속성 데이터 테이블 펼침
            if (setIsAttributeTableExpanded) {
                setIsAttributeTableExpanded(true); 
            }    
        },
        [setSelectedLayerIndex, setExpandedMap, setIsAttributeTableExpanded]
    );

    const handleClassificationChange = useCallback(
        (index, patch) => {
            const next = {
                ...layerUtils.ensureClassification(layers[index]),
                ...patch,
            };
            updateLayer(index, { classification: next });
        },
        [layers, updateLayer]
    );

    const handleLegendChange = useCallback(
        (index, geometry, patch) => {
            const current = layerUtils.ensureLegend(layers[index]);
            updateLayer(index, {
                styles: {
                    ...current,
                    [geometry]: {
                        ...current[geometry],
                        ...patch,
                    },
                },
            });
        },
        [layers, updateLayer]
    );

    const handleTableView = (index) => {
        if (index === selectedLayerIndex) {
            // 동일 레이어 재클릭 -> 닫기
            setSelectedLayerIndex(null); 
            setIsAttributeTableExpanded(false); 
        } else {
            // 다른 레이어 클릭 -> 새 레이어 선택 및 펼치기
            setSelectedLayerIndex(index); 
            setIsAttributeTableExpanded(true); 
        }
    };

    const handleClassificationApply = useCallback(
        (index, geometrySet = new Set()) => {
            const layer = layers[index];
            if (!layer?.geojson) return;
            const classification = layerUtils.ensureClassification(layer);
            if (!classification.field) return;
            const features = layer.geojson.features ?? [];
            const values = features
                .map((feature) => Number(feature?.properties?.[classification.field]))
                .filter((value) => Number.isFinite(value));
            if (!values.length) return;
            const palette = config.colorPalettes.find((item) => item.id === classification.palette) ?? config.colorPalettes[0];
            const maxSteps = palette.colors.length;
            const effectiveSteps = Math.max(1, Math.min(classification.steps || maxSteps, maxSteps));
            const breaks = layerUtils.computeClassificationBreaks(values, effectiveSteps, classification.method, classification.customBreaks);
            if (!breaks) return;
            const colors = palette.colors.slice(0, effectiveSteps);
            const classificationConfig = {
                field: classification.field,
                breaks,
                colors,
            };
            const updatedStyles = { ...layer.styles };
            const targets = geometrySet.size ? Array.from(geometrySet) : ['point', 'line', 'polygon'];
            targets.forEach((geom) => {
                if (updatedStyles[geom]) {
                    updatedStyles[geom] = {
                        ...updatedStyles[geom],
                        classification: classificationConfig,
                    };
                }
            });
            updateLayer(index, {
                styles: updatedStyles,
                classification: { ...classification, steps: effectiveSteps, appliedAt: Date.now() },
                classificationLegend: classificationConfig,
            });
        },
        [layers, updateLayer]
    );

    const handleLayerDragStart = (index) => (evt) => {
        setDraggedIndex(index);
        evt.dataTransfer.effectAllowed = 'move';
        evt.dataTransfer.setData('text/plain', String(index));
    };

    const handleLayerDragOver = (index) => (evt) => {
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'move';
        if (draggedIndex === null || draggedIndex === index) return;
    };

    const handleLayerDrop = (index) => (evt) => {
        evt.preventDefault();
        const startIndex = draggedIndex ?? Number(evt.dataTransfer.getData('text/plain'));
        if (Number.isNaN(startIndex) || startIndex === index) return;
        reorderLayers(startIndex, index);
        setDraggedIndex(null);
    };

    const handleDragEnd = () => setDraggedIndex(null);

    return (
        <div>
            {/* 레이어 헤더 및 버튼 */}
            <div css={s.layerHeader}>
                <span css={s.layerTitle}>레이어</span>
                <button css={s.dataAddButton} onClick={openUploadModal}>
                    <PlusIcon style={{ width: '14px', height: '14px' }} />
                    데이터 추가
                </button>
            </div>
            
            {/* 레이어 목록 */}
            <Paper css={s.panelStyles} elevation={0}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" className="panel-header">
                    <Box>
                        <Typography variant="h6" className="panel-title">
                            레이어 목록
                        </Typography>
                    </Box>
                    <Typography className="panel-count">{layers.length} layers</Typography>
                </Stack>
    
                {layers.length === 0 ? (
                    <Paper variant="outlined" className="empty-state">
                        <Typography variant="body1">등록된 레이어가 없습니다.</Typography>
                        <Typography variant="body2">우측 상단의 데이터 추가 버튼을 통해 데이터를 업로드하세요.</Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2} className="layer-stack">
                        {layers.map((layer, index) => {
                            const isExpanded = !!expandedMap[index];
                            const classification = layerUtils.ensureClassification(layer);
                            const legend = layerUtils.ensureLegend(layer);
                            const geometrySet = geometryPresence[index] ?? new Set();
                            const showPointControls = geometrySet.size === 0 || geometrySet.has('point');
                            const showLineControls = geometrySet.size === 0 || geometrySet.has('line');
                            const showPolygonControls = geometrySet.size === 0 || geometrySet.has('polygon');
                            const isSelected = selectedLayerIndex === index;
                            return (
                                <div
                                    key={layer.name + index}
                                    draggable
                                    onDragStart={handleLayerDragStart(index)}
                                    onDragOver={handleLayerDragOver(index)}
                                    onDrop={handleLayerDrop(index)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <Paper
                                        variant="outlined"
                                        elevation={0}
                                        className={`layer-card ${isSelected ? 'selected' : ''} ${!isExpanded ? 'collapsed' : ''}`}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} className="layer-header">
                                            <IconButton className="drag-handle" size="small">
                                                <DragIndicator fontSize="small" />
                                            </IconButton>
                                            <Box
                                                className="layer-info"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleLayerSelect(index)}
                                                onKeyDown={() => handleLayerSelect(index)}
                                            >
                                                <div className="layer-info-header">
                                                    {!!geometrySet.size && (
                                                        <div className="geometry-icons">
                                                            {config.GEOMETRY_ORDER.filter((type) => geometrySet.has(type)).map((type) => (
                                                                <span
                                                                    key={type}
                                                                    className={`geometry-icon ${config.geometryIconClassMap[type] ?? 'fg fg-layer-poi'}`}
                                                                    title={config.geometryLabelMap[type]}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                    <span className="layer-name">{layer.name}</span>
                                                </div>
                                            </Box>
                                            <Box className="layer-actions">
                                                {/* 테이블 버튼 */}
                                                <IconButton
                                                    size="small"
                                                    className="icon-button"
                                                    onClick={() => handleTableView(index)}
                                                    aria-label="open attributes"
                                                >
                                                    <TableChart fontSize="small" />
                                                </IconButton>
                                                {/* 레이어 보기 버튼 */}
                                                <IconButton
                                                    size="small"
                                                    className="icon-button"
                                                    onClick={() => handleVisibilityToggle(index)}
                                                    aria-label={layer.visible ? 'hide layer' : 'show layer'}
                                                >
                                                    {layer.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                                </IconButton>
                                                {/* 레이어 삭제 버튼 */}
                                                <IconButton
                                                    size="small"
                                                    className="icon-button"
                                                    onClick={() => removeLayer(index)}
                                                    aria-label="remove layer"
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                                <Divider orientation="vertical" flexItem className="action-divider" />
                                                <IconButton
                                                    size="small"
                                                    className="icon-button expand-toggle"
                                                    onClick={() => toggleExpand(index)}
                                                    aria-label={isExpanded ? 'collapse layer' : 'expand layer'}
                                                >
                                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            </Box>
                                        </Stack>
    
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Stack spacing={2} className="layer-body">
    
                                            <section className="section">
                                                <div className="section-header">
                                                    <div className="section-title">
                                                        <span>범례 스타일</span>
                                                    </div>
                                                </div>
                                                <div className="style-grid">
                                                    {showPointControls && (
                                                        <div className="geometry-block">
                                                            <div className="dual-row">
                                                                <TextField
                                                                    className="color-field"
                                                                    type="color"
                                                                    size="small"
                                                                    label="색상"
                                                                    value={legend.point.fill}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    onChange={(evt) =>
                                                                        handleLegendChange(index, 'point', {
                                                                            fill: evt.target.value,
                                                                        })
                                                                    }
                                                                />
                                                                <TextField
                                                                    className="color-field"
                                                                    type="color"
                                                                    size="small"
                                                                    label="테두리"
                                                                    value={legend.point.stroke}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    onChange={(evt) =>
                                                                        handleLegendChange(index, 'point', {
                                                                            stroke: evt.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>반경</span>
                                                                    <span>{legend.point.radius}px</span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={4}
                                                                    max={18}
                                                                    value={legend.point.radius}
                                                                    valueLabelDisplay="auto"
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'point', {
                                                                            radius: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>투명도</span>
                                                                    <span>{Math.round((legend.point.opacity ?? 1) * 100)}%</span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={0}
                                                                    max={1}
                                                                    step={0.05}
                                                                    value={legend.point.opacity ?? 1}
                                                                    valueLabelDisplay="auto"
                                                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'point', {
                                                                            opacity: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
    
                                                    {showLineControls && (
                                                        <div className="geometry-block">
                                                            <div className="dual-row">
                                                                <TextField
                                                                    className="color-field"
                                                                    type="color"
                                                                    size="small"
                                                                    label="색상"
                                                                    value={legend.line.stroke}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    onChange={(evt) =>
                                                                        handleLegendChange(index, 'line', {
                                                                            stroke: evt.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>두께</span>
                                                                    <span>{legend.line.width}px</span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={1}
                                                                    max={10}
                                                                    value={legend.line.width}
                                                                    valueLabelDisplay="auto"
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'line', {
                                                                            width: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>투명도</span>
                                                                    <span>{Math.round((legend.line.opacity ?? 1) * 100)}%</span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={0}
                                                                    max={1}
                                                                    step={0.05}
                                                                    value={legend.line.opacity ?? 1}
                                                                    valueLabelDisplay="auto"
                                                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'line', {
                                                                            opacity: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
    
                                                    {showPolygonControls && (
                                                        <div className="geometry-block">
                                                            <div className="dual-row">
                                                                <TextField
                                                                    className="color-field"
                                                                    type="color"
                                                                    size="small"
                                                                    label="내부 색상"
                                                                    value={legend.polygon.fill}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    onChange={(evt) =>
                                                                        handleLegendChange(index, 'polygon', {
                                                                            fill: evt.target.value,
                                                                        })
                                                                    }
                                                                />
                                                                <TextField
                                                                    className="color-field"
                                                                    type="color"
                                                                    size="small"
                                                                    label="테두리"
                                                                    value={legend.polygon.stroke}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    onChange={(evt) =>
                                                                        handleLegendChange(index, 'polygon', {
                                                                            stroke: evt.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>테두리 두께</span>
                                                                    <span>{legend.polygon.width}px</span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={1}
                                                                    max={10}
                                                                    value={legend.polygon.width}
                                                                    valueLabelDisplay="auto"
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'polygon', {
                                                                            width: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="slider-field">
                                                                <div className="slider-label">
                                                                    <span>투명도</span>
                                                                    <span>
                                                                        {Math.round((legend.polygon.opacity ?? 1) * 100)}%
                                                                    </span>
                                                                </div>
                                                                <Slider
                                                                    size="small"
                                                                    min={0}
                                                                    max={1}
                                                                    step={0.05}
                                                                    value={legend.polygon.opacity ?? 1}
                                                                    valueLabelDisplay="auto"
                                                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                                                    onChange={(_, value) =>
                                                                        handleLegendChange(index, 'polygon', {
                                                                            opacity: Array.isArray(value) ? value[0] : value,
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                </section>
                                                <section className="section classification-section">
                                                    <div className="section-header">
                                                        <div className="section-title">
                                                            <Tune fontSize="small" />
                                                            <span>단계 구분 값</span>
                                                        </div>
                                                    </div>
                                                    <div className="classification-grid">
                                                        <TextField
                                                            select
                                                            size="small"
                                                            label="기준 필드"
                                                            value={classification.field}
                                                            onChange={(evt) =>
                                                                handleClassificationChange(index, { field: evt.target.value })
                                                            }
                                                            fullWidth
                                                        >
                                                            <MenuItem value="">
                                                                <em>필드 선택</em>
                                                            </MenuItem>
                                                            {(propertyOptions[index] ?? []).map((field) => (
                                                                <MenuItem key={field} value={field}>
                                                                    {field}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </div>
                                                    <div className="classification-grid secondary">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            label="구간 수"
                                                            value={classification.steps}
                                                            inputProps={{ min: 3, max: 9 }}
                                                            onChange={(evt) =>
                                                                handleClassificationChange(index, {
                                                                    steps: Number(evt.target.value) || 3,
                                                                })
                                                            }
                                                        />
                                                        <TextField
                                                            select
                                                            size="small"
                                                            label="분류 방법"
                                                            value={classification.method}
                                                            onChange={(evt) =>
                                                                handleClassificationChange(index, { method: evt.target.value })
                                                            }
                                                        >
                                                            {config.classificationMethods.map((method) => (
                                                                <MenuItem key={method.id} value={method.id}>
                                                                    {method.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </div>
                                                    {classification.method === 'custom' && (
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            label="사용자 구간 (쉼표로 구분)"
                                                            placeholder="예) 0,10,20,40"
                                                            value={classification.customBreaks}
                                                            onChange={(evt) =>
                                                                handleClassificationChange(index, {
                                                                    customBreaks: evt.target.value,
                                                                })
                                                            }
                                                        />
                                                    )}
                                                    <div className="palette-row">
                                                        <TextField
                                                            select
                                                            size="small"
                                                            label="색상 팔레트"
                                                            value={classification.palette}
                                                            onChange={(evt) =>
                                                                handleClassificationChange(index, { palette: evt.target.value })
                                                            }
                                                            SelectProps={{
                                                                renderValue: (value) => {
                                                                    const palette = config.colorPalettes.find((p) => p.id === value);
                                                                    if (!palette) {
                                                                        return value || '';
                                                                    }
                                                                    return (
                                                                        <Box sx={config.paletteValueSx}>
                                                                            <span>{palette.label}</span>
                                                                            <Box sx={config.paletteSwatchSx}>
                                                                                {palette.colors.map((color) => (
                                                                                    <Box key={color} sx={{ ...config.paletteChipSx, background: color }} />
                                                                                ))}
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                },
                                                            }}
                                                        >
                                                            {config.colorPalettes.map((palette) => (
                                                                <MenuItem key={palette.id} value={palette.id}>
                                                                    <Box sx={config.paletteValueSx}>
                                                                        <span>{palette.label}</span>
                                                                        <Box sx={config.paletteSwatchSx}>
                                                                            {palette.colors.map((color) => (
                                                                                <Box key={color} sx={{ ...config.paletteChipSx, background: color }} />
                                                                            ))}
                                                                        </Box>
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </div>
                                                    <div className="classification-actions">
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => handleClassificationApply(index, geometrySet)}
                                                            disabled={!classification.field || !layer?.geojson?.features?.length}
                                                            className="classification-apply-button"
                                                        >
                                                            단계 구분 적용
                                                        </Button>
                                                    </div>
                                                </section>
                                            </Stack>
                                        </Collapse>
                                    </Paper>
                                </div>
                            );
                        })}
                    </Stack>
                )}
            </Paper>

            <input
                hidden
                ref={fileInputRef}
                accept=".zip,.shp,.json,.geojson"
                type="file"
                onChange={handleFileUpload}
            />

            {isUploadModalOpen && 
                <DataAddModal 
                    isOpen={isUploadModalOpen}
                    onClose={closeUploadModal}
                    fileInputRef={fileInputRef}
                    isDragActive={isDragActive}
                    handleDrop={handleDrop}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    handleClick={handleClick}
                    handleDownload1={handleDownload1}
                    handleDownload2={handleDownload2}
                />
            }
        </div>

    );
};

export default LayerContent;
